/**
 * +specs: the hero's specialisation (docs/design/classes/specs.md "Progression"). Chosen once the
 * hero is level 10, standing at the Tower Warden (the Advancement Trial); the first choice is free,
 * a respec costs RESPEC_GOLD. Server-authoritative through the `chooseSpec` intent. Pure: returns a
 * new Profile, never mutates its input.
 */
import type { ClassDef, ClassId, DungeonDef, Role } from '@keystone/sim';
import { applySpec } from '@keystone/sim';
import { FIELD_NPC, SPEC_LEVEL, getContentBundle, specDef } from '@keystone/content';
import type { Profile } from './types';

/** Gold a respec costs (the first choice is free). */
export const RESPEC_GOLD = 60;
/** How close (yards) the hero must stand to the Tower Warden to choose. */
export const SPEC_WARDEN_RANGE = 15;
/** The NPC who runs the Advancement Trial. */
export const SPEC_NPC_ID: string = FIELD_NPC.towerWarden;

export type SpecFail = { ok: false; reason: string };
export type SpecOp = { ok: true; profile: Profile; specId: string; previous: string | null; cost: number } | SpecFail;

const fail = (reason: string): SpecFail => ({ ok: false, reason });

/** The hero's chosen spec id (null before the Tower, or with no character). */
export function heroSpecId(p: Pick<Profile, 'character' | 'characters'>): string | null {
  const c = p.character;
  return (c && p.characters[c.classId]?.hero.specId) || null;
}

/** A class's spec id on the profile (null = none chosen). */
export function classSpecId(p: Pick<Profile, 'characters'>, classId: ClassId): string | null {
  return p.characters[classId]?.hero.specId ?? null;
}

/** The hero's role: the spec's when chosen, else the base class's. */
export function heroRole(p: Pick<Profile, 'character' | 'characters'>): Role {
  const c = p.character;
  if (!c) return 'dps';
  const s = specDef(heroSpecId(p));
  return s?.role ?? getContentBundle().classes.find((k) => k.id === c.classId)?.role ?? 'dps';
}

/** The base ClassDef with the class's chosen spec folded in (unchanged when none / unknown). */
export function withSpec(p: Pick<Profile, 'characters'>, classDef: ClassDef): ClassDef {
  const s = specDef(classSpecId(p, classDef.id));
  return s && s.baseClassId === classDef.id && !classDef.specId ? applySpec(classDef, s) : classDef;
}

function wardenOf(dungeons: readonly DungeonDef[]): { mapId: string; pos: { x: number; y: number } } | null {
  for (const d of dungeons) {
    const n = d.field?.npcs?.find((x) => x.id === SPEC_NPC_ID);
    if (n) return { mapId: d.id, pos: n.pos };
  }
  return null;
}

/** Why the hero can't take `specId` right now (null = it can). */
export function specRefusal(p: Profile, specId: string, dungeons: readonly DungeonDef[] = getContentBundle().dungeons): string | null {
  const c = p.character;
  if (!c) return 'No character';
  const ch = p.characters[c.classId];
  if (!ch) return 'No character';
  const s = specDef(specId);
  if (!s) return 'That specialisation is not ready yet';
  if (s.baseClassId !== c.classId) return `Only a ${c.classId} can become a ${s.name}`;
  if (ch.hero.level < Math.max(SPEC_LEVEL, s.unlockLevel)) return `Reach level ${Math.max(SPEC_LEVEL, s.unlockLevel)} first`;
  const w = wardenOf(dungeons);
  if (w && (c.mapId !== w.mapId || !c.pos || Math.hypot(c.pos.x - w.pos.x, c.pos.y - w.pos.y) > SPEC_WARDEN_RANGE)) return 'Speak to Warden Ilsa on the Tower Road';
  if (ch.hero.specId === specId) return `You are already a ${s.name}`;
  if (ch.hero.specId && (p.currencies.gold ?? 0) < RESPEC_GOLD) return `A new path costs ${RESPEC_GOLD} gold`;
  return null;
}

/** Take (or change to) a specialisation at the Tower Warden. Respec costs RESPEC_GOLD. */
export function chooseSpec(profile: Profile, specId: string, dungeons: readonly DungeonDef[] = getContentBundle().dungeons): SpecOp {
  const why = specRefusal(profile, specId, dungeons);
  if (why) return fail(why);
  const p = structuredClone(profile);
  const ch = p.characters[p.character!.classId]!;
  const previous = ch.hero.specId ?? null;
  const cost = previous ? RESPEC_GOLD : 0;
  p.currencies.gold -= cost;
  ch.hero.specId = specId;
  return { ok: true, profile: p, specId, previous, cost };
}
